import { LinkedProjectConfig, ServiceTransaction } from '../types';
import {
    isJournalMappable,
    kennungFuerVergleich,
    loadJournal,
    meldeSpiegelFehler,
    mirrorEntriesToJournalOhneKorb,
    toJournalRows,
    voidJournalEntries,
    type JournalMirrorRequest,
    type JournalMirrorResult,
    type JournalVoidResult,
    type ServiceJournalRow,
} from './serviceJournal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE JOURNAL-HAELFTE DES PROJEKTWECHSELS (Verknuepfen / Trennen)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Beim Verknuepfen (`applyLinkTransfer`) wandern Protokollzeilen der Tochter
 * an die Mutter, beim Trennen (`applyLinkReturn`) zurueck. Im JSON ist das ein
 * Umzug zwischen ZWEI Projekten; das Journal kennt weder Update noch Delete und
 * fuehrt `project_id` als Spalte. Dort ist der Umzug deshalb ANHANG im Ziel
 * plus STILLLEGEN in der Quelle (`service_journal_void`, Grund `correction`).
 *
 * ─── Was bis 02.10.2026 hier stand, und warum es falsch war ──────────────
 * Beide Aufrufstellen spiegelten in zwei blinden Schritten:
 * `mirrorEntriesToJournal` (mit Ausgangskorb) ins Ziel, danach
 * `voidJournalEntries(quelle, movedEntryIds)` mit den JSON-ids. Dieselbe
 * Fehlerklasse wie beim Zuordnungsumzug innerhalb eines Projekts
 * (backfillMoveJournal.ts, Faelle 5633 und 6263):
 *   1. Stilllegen ueber die JSON-id trifft bei zwei lebenden Zeilen, die auf
 *      dieselbe id antworten, nach Rangfolge EINE — nicht unbedingt die
 *      richtige, und die zweite bleibt lebend stehen.
 *   2. Verknuepfen → Trennen → Verknuepfen: der abgeleitete Schluessel der
 *      Zielzeile (md5 ueber den Inhalt samt `projectId`, `deterministicEntryId`)
 *      ist beim dritten Umzug derselbe wie beim ersten — und der wurde beim
 *      Trennen STILLGELEGT. Das Anhaengen schreibt nichts (`on conflict do
 *      nothing`, trotzdem `written: true`), das Stilllegen danach nimmt in der
 *      Quelle die letzte lebende Zeile weg: KEINE lebende Zeile mehr, in
 *      keinem der beiden Projekte.
 *
 * `ersetzeJournalzeilen` (SQL Nr. 136) hilft hier nicht: es ersetzt in EINER
 * Transaktion innerhalb EINES Projekts. Ein projektuebergreifendes Ersetzen
 * gibt es nicht, und eine neue RPC dafuer braeuchte eine Migration (bis die
 * gefahren ist, liefe der Client auf genau den unsicheren Weg zurueck).
 *
 * ─── Was jetzt geschieht ──────────────────────────────────────────────────
 *   1. Die QUELLE einmal lesen und zu jeder gewanderten JSON-id die lebenden
 *      Zeilen sammeln (`id` oder `legacy_entry_id`) — als JOURNAL-ids.
 *   2. Im ZIEL anhaengen, ohne Korb.
 *   3. Das ZIEL frisch lesen: lebt die erwartete Zielzeile jeder gewanderten
 *      Zeile wirklich?
 *   4. NUR fuer Zeilen, deren Zielzeile lebt, die gesammelten Quellzeilen
 *      ueber ihre JOURNAL-ids stilllegen (`nurJournalIds`).
 * Alles andere bleibt in der Quelle lebend stehen: ein falscher Ort, aber
 * keine verlorene Menge — der Nachtlauf zeigt die Zeile an der Quelle.
 *
 * ─── Die Ausfallrichtung ──────────────────────────────────────────────────
 * Zwei lebende Zeilen ueber zwei Projekte sind SICHTBAR (Nachtlauf,
 * Abweichungssicht). Eine faelschlich stillgelegte Zeile ist STILL — die
 * schlechtere Richtung. Deshalb wird im Zweifel nie stillgelegt.
 */

export interface ProjektwechselSpiegeln {
    /**
     * Das Projekt, das die Zeilen ABGIBT. Leer oder `null`: nur anhaengen,
     * nichts stilllegen — der Altaufrufer von `applyLinkTransfer` ohne
     * Tochter-Kennung, wie bisher.
     */
    quelleProjectId: string | null | undefined;
    /** Das Projekt, das die Zeilen AUFNIMMT. */
    zielProjectId: string;
    /**
     * Die gewanderten Zeilen GENAU so, wie sie jetzt im Ziel gespeichert sind
     * (beim Verknuepfen in der Kind-Form). Aus ihnen wird der Zielschluessel
     * gerechnet — eine andere Fassung ergaebe einen anderen md5.
     */
    entries: ServiceTransaction[];
    /**
     * Das Protokoll des Ziels fuer `knownIds` — ausdruecklich EINSCHLIESSLICH
     * der gewanderten Zeilen: ihre ids sind als Journalschluessel in der Quelle
     * schon vergeben, ohne diesen Hinweis verschluckte `on conflict do nothing`
     * sie still.
     */
    zielPreviousLog: ServiceTransaction[];
    /**
     * Was in der Quelle STEHEN BLEIBT. Traegt eine dieser Zeilen die JSON-id
     * einer gewanderten (doppelte id, `doppelt_im_projekt`), antworten die
     * lebenden Quellzeilen zu dieser id auch fuer die bleibende — stillgelegt
     * wird dann zu dieser id nichts.
     */
    quelleVerbleibend?: ServiceTransaction[] | null;
    linkedProjectConfig: LinkedProjectConfig | null;
    source: 'link_transfer';
}

export interface ProjektwechselErgebnis {
    /** JSON-ids: die Zielzeile lebt, die Quellzeile(n) sind stillgelegt. */
    verschoben: string[];
    /**
     * JSON-ids: angehaengt (die Zielzeile lebt, oder das Ziel war danach nicht
     * lesbar), in der Quelle aber NICHTS stillgelegt.
     */
    nurAngehaengt: Array<{ id: string; grund: string }>;
    /** JSON-ids: im Ziel lebt keine Zeile — die Quellzeile bleibt lebend. */
    nichtGeschrieben: Array<{ id: string; grund: string }>;
}

const kennung = (e: ServiceTransaction | null | undefined): string => String(e?.id ?? '').trim();

/**
 * Vergleichsform einer Kennung: eine `uuid` kommt aus Postgres kleingeschrieben,
 * der JSON-Rohwert nicht immer. Dieselbe Regel wie im Journal
 * (`kennungFuerVergleich`): nur die uuid wird kleingeschrieben, eine
 * Alt-Kennung bleibt exakt (Codex-Review 02.10.2026, P2).
 */
const klein = (v: unknown): string => kennungFuerVergleich(v);

const anzeige = (id: string): string => id || '(ohne id)';

/**
 * Die im Ziel bereits vergebenen ids — WORTGLEICH wie `idsOf` in
 * serviceJournal.ts, denn daraus rechnet das Anhaengen seine Schluessel. Eine
 * abweichende Fassung erwartete hier einen anderen Schluessel als den, der
 * geschrieben wurde.
 */
const idsOf = (log: ServiceTransaction[] | null | undefined): string[] =>
    (log ?? []).map(e => (typeof e?.id === 'string' ? e.id.trim() : '')).filter(Boolean);

// Die drei Netzwege, jeder gegen einen Wurf gesichert. Keiner von ihnen wirft
// heute — aber diese Funktion verspricht es auch dann, wenn sich das aendert,
// und ein Wurf mitten im Ablauf darf nie in ein Stilllegen ohne Pruefung
// fuehren.
const ladeSicher = async (projectId: string): Promise<ServiceJournalRow[] | null> => {
    try {
        return await loadJournal(projectId);
    } catch (e) {
        console.warn(`[linkTransferJournal] Journal von ${projectId} nicht lesbar:`, e);
        return null;
    }
};

const anhaengenSicher = async (req: JournalMirrorRequest): Promise<JournalMirrorResult> => {
    try {
        return await mirrorEntriesToJournalOhneKorb(req);
    } catch (e) {
        return { written: false, reason: 'fehler', error: e, rows: 0, skipped: [] };
    }
};

const stilllegenSicher = async (projectId: string, ids: string[]): Promise<JournalVoidResult> => {
    try {
        return await voidJournalEntries(projectId, ids, 'correction', { nurJournalIds: true });
    } catch (e) {
        return { voided: 0, unbekannt: [], journalGelesen: true, error: e };
    }
};

/**
 * Spiegelt einen bestaetigt gespeicherten Projektwechsel ins Journal.
 *
 * Wirft nie: das JSON beider Projekte ist zu diesem Zeitpunkt geschrieben, der
 * Vorgang ist gelungen. Ein Wurf zeigte einen Fehler zu einem Umzug, der
 * stattgefunden hat. Eine Restabweichung zeigt der Nachtlauf.
 */
export const spiegleProjektwechsel = async (args: ProjektwechselSpiegeln): Promise<ProjektwechselErgebnis> => {
    const entries = (args?.entries ?? []).filter(Boolean);
    if (entries.length === 0) return { verschoben: [], nurAngehaengt: [], nichtGeschrieben: [] };
    const stand = { rpcGeschrieben: false };
    try {
        return await spiegeln(args, entries, stand);
    } catch (e) {
        // Nur noch eigene Rechenfehler koennen hier landen, die Netzwege sind
        // gesichert. Das Stilllegen ist der letzte Netzweg, ein Wurf davor legt
        // also nichts still; was angehaengt ist, weiss nur die RPC.
        const grund = `unerwarteter Fehler: ${e instanceof Error ? e.message : String(e)}`;
        console.warn(
            '[linkTransferJournal] Projektwechsel nicht vollständig gespiegelt — unerwarteter Fehler. '
            + 'Die Quellzeilen bleiben lebend, der Nachtlauf zeigt sie.',
            e,
        );
        // Die Kennung selbst kann der Ausloeser gewesen sein — hier darf sie
        // nicht noch einmal werfen.
        const alle = entries.map(eintrag => {
            try {
                return { id: anzeige(kennung(eintrag)), grund };
            } catch {
                return { id: '(unlesbar)', grund };
            }
        });
        return stand.rpcGeschrieben
            ? { verschoben: [], nurAngehaengt: alle, nichtGeschrieben: [] }
            : { verschoben: [], nurAngehaengt: [], nichtGeschrieben: alle };
    }
};

const spiegeln = async (
    args: ProjektwechselSpiegeln,
    entries: ServiceTransaction[],
    stand: { rpcGeschrieben: boolean },
): Promise<ProjektwechselErgebnis> => {
    const quelle = String(args.quelleProjectId ?? '').trim();
    const ziel = String(args.zielProjectId ?? '').trim();
    const { linkedProjectConfig, source } = args;
    const ergebnis: ProjektwechselErgebnis = { verschoben: [], nurAngehaengt: [], nichtGeschrieben: [] };

    // ── Der erwartete Zielschluessel jeder gewanderten Zeile ────────────────
    // Dieselbe Rechnung wie das Anhaengen (`spiegelnOhneKorb`): ein Aufruf
    // ueber ALLE abbildbaren Zeilen mit den vergebenen ids des Ziels. Im
    // Stapel und nicht je Zeile, weil `toJournalRows` innerhalb des Stapels
    // weitervergibt — nur so ist der erwartete Schluessel der geschriebene.
    // `toJournalRows` laesst nicht abbildbare Zeilen aus; der Index laeuft
    // deshalb ueber die vorher gefilterte Liste.
    const abbildbar = entries.filter(isJournalMappable);
    const zeilen = toJournalRows({
        projectId: ziel,
        entries: abbildbar,
        source,
        linkedProjectConfig,
        knownIds: idsOf(args.zielPreviousLog),
    });
    const zielzeileVon = new Map<ServiceTransaction, ServiceJournalRow>();
    abbildbar.forEach((e, i) => { if (zeilen[i]) zielzeileVon.set(e, zeilen[i]); });

    // ── 1. Die Quelle EINMAL lesen ──────────────────────────────────────────
    // Zu jeder gewanderten JSON-id die LEBENDEN Zeilen, die auf sie antworten
    // (`id` oder `legacy_entry_id`), als JOURNAL-ids. Gelesen VOR dem
    // Anhaengen: das Anhaengen schreibt in ein anderes Projekt und aendert an
    // diesem Stand nichts. Nicht lesbar: angehaengt wird trotzdem (das JSON ist
    // umgezogen, das Ziel braucht seine Zeile), stillgelegt wird nichts.
    const gesucht = new Set(entries.map(kennung).filter(Boolean).map(klein));
    let quellzeilen: Map<string, string[]> | null = null;
    if (quelle && gesucht.size > 0) {
        const journal = await ladeSicher(quelle);
        if (journal) {
            quellzeilen = new Map();
            for (const row of journal) {
                if (row.voided_at) continue;
                for (const schluessel of new Set([klein(row.id), klein(row.legacy_entry_id)])) {
                    if (!schluessel || !gesucht.has(schluessel)) continue;
                    const liste = quellzeilen.get(schluessel) ?? [];
                    if (!liste.includes(row.id)) liste.push(row.id);
                    quellzeilen.set(schluessel, liste);
                }
            }
        } else {
            console.warn(
                `[linkTransferJournal] Journal der Quelle ${quelle} nicht lesbar — die gewanderten Zeilen werden `
                + 'im Ziel angehängt, in der Quelle aber NICHT stillgelegt. Zwei lebende Zeilen über zwei Projekte '
                + 'zeigt der Nachtlauf.',
            );
        }
    }

    // ── 2. Anhaengen im Ziel — OHNE Korb ────────────────────────────────────
    // Die Antwort muss ENDGUELTIG sein, denn an ihr haengt, ob in der Quelle
    // stillgelegt wird. Aus dem Korb kaeme eine gescheiterte Zeile Stunden
    // spaeter nach — nachdem die Quelle hier bewusst NICHT stillgelegt wurde
    // (dann lebt sie doppelt), oder nach einem Trennen, das die Zeile laengst
    // zurueckgeholt hat (dann stuende im frueheren Ziel eine zweite lebende
    // neben der in der Quelle). Scheitert es hier, bleibt die Quellzeile allein
    // und lebend stehen, und der Nachtlauf zeigt sie am falschen Ort.
    const angehaengt = await anhaengenSicher({
        projectId: ziel,
        entries,
        previousLog: args.zielPreviousLog,
        source,
        linkedProjectConfig,
    });
    stand.rpcGeschrieben = angehaengt.written;

    // ── 3. Das Ziel frisch lesen ────────────────────────────────────────────
    // `written` heisst nur „die RPC hat nicht geworfen": ein schon
    // stillgelegter Schluessel (Verknuepfen → Trennen → Verknuepfen) wird von
    // `on conflict do nothing` still verworfen. Was zaehlt, ist allein, ob die
    // Zielzeile JETZT lebt. Nicht lesbar: nichts stilllegen.
    //
    // Gemerkt wird je lebender id auch ihre `legacy_entry_id`: zwei gewanderte
    // Zeilen mit VERSCHIEDENEN JSON-ids, aber gleichem Inhalt bekommen im Ziel
    // denselben Inhaltsschluessel, und nur die erste kommt an. Lebt der
    // Schluessel, aber mit der legacy-id der anderen, ist DIESE Zeile nicht
    // angekommen — ihre Quellzeile stillzulegen hiesse, eine Menge zu verlieren.
    let zielLebend: Map<string, string> | null = null;
    if (angehaengt.written) {
        const journal = await ladeSicher(ziel);
        if (journal) {
            zielLebend = new Map(journal.filter(r => !r.voided_at).map(r => [klein(r.id), klein(r.legacy_entry_id)]));
        } else {
            console.warn(
                `[linkTransferJournal] Journal des Ziels ${ziel} nach dem Anhängen nicht lesbar — in der Quelle `
                + `${quelle || '(keine)'} wird NICHTS stillgelegt. Zwei lebende Zeilen über zwei Projekte sind `
                + 'sichtbar, eine fälschlich stillgelegte wäre es nicht.',
            );
        }
    } else if (angehaengt.reason !== 'leer') {
        // `fehler` hat `appendToJournal` schon gemeldet, `rpc_fehlt` ist der
        // geplante Zustand vor Nr. 61 — beides nur ein Satz in der Konsole.
        console.warn(
            `[linkTransferJournal] Anhängen im Ziel ${ziel} gescheitert (${angehaengt.reason ?? 'unbekannt'}) — `
            + `in der Quelle ${quelle || '(keine)'} wird nichts stillgelegt, die Zeilen bleiben dort lebend.`,
            angehaengt.error,
        );
    }

    // ── 4. Je Zeile entscheiden ─────────────────────────────────────────────
    // Wie oft steht jede id unter den gewanderten Zeilen und in der Quelle?
    const anzahl = new Map<string, number>();
    for (const e of entries) {
        const id = klein(kennung(e));
        if (id) anzahl.set(id, (anzahl.get(id) ?? 0) + 1);
    }
    const verbleibend = new Set((args.quelleVerbleibend ?? []).map(e => klein(kennung(e))).filter(Boolean));

    const nichtLebend: Array<{ id: string; zeile: ServiceJournalRow | null; grund: string }> = [];
    const mehrdeutig: string[] = [];
    /** JSON-id (Anzeige) → Journal-ids der Quelle, die stillgelegt werden sollen. */
    const kandidaten: Array<{ id: string; journalIds: string[] }> = [];

    for (const e of entries) {
        const roh = kennung(e);
        const id = anzeige(roh);
        const zeile = zielzeileVon.get(e) ?? null;

        if (!zeile) {
            const grund = 'nicht abbildbar (Position, Menge oder Datum fehlt)';
            ergebnis.nichtGeschrieben.push({ id, grund });
            nichtLebend.push({ id, zeile: null, grund });
            continue;
        }
        if (!angehaengt.written) {
            ergebnis.nichtGeschrieben.push({ id, grund: `Anhängen gescheitert (${angehaengt.reason ?? 'unbekannt'})` });
            continue;
        }
        if (!zielLebend) {
            ergebnis.nurAngehaengt.push({ id, grund: 'Ziel nach dem Anhängen nicht lesbar' });
            continue;
        }
        const lebendeLegacy = zielLebend.get(klein(zeile.id));
        if (lebendeLegacy === undefined || lebendeLegacy !== klein(zeile.legacy_entry_id)) {
            const grund = lebendeLegacy === undefined
                ? `die Zielzeile ${zeile.id} lebt nach dem Anhängen nicht (Schlüssel schon stillgelegt)`
                : `der Zielschlüssel ${zeile.id} gehört einer inhaltsgleichen Zeile (${lebendeLegacy || 'ohne legacy'})`;
            ergebnis.nichtGeschrieben.push({ id, grund });
            nichtLebend.push({ id, zeile, grund });
            continue;
        }

        // Ab hier lebt die Zielzeile. Stillgelegt wird nur, was eindeutig zu
        // GENAU dieser Zeile gehoert.
        if (!quelle) {
            ergebnis.nurAngehaengt.push({ id, grund: 'keine Quelle angegeben' });
            continue;
        }
        if (!roh) {
            // Ohne JSON-id traegt die Quellzeile einen Inhaltsschluessel ueber die
            // Fassung VOR dem Umzug — von hier aus nicht zuzuordnen. Das war
            // schon vorher so (`voidJournalEntries` laesst leere ids aus).
            ergebnis.nurAngehaengt.push({ id, grund: 'ohne JSON-id — die Quellzeile ist nicht zuzuordnen' });
            continue;
        }
        if (!quellzeilen) {
            ergebnis.nurAngehaengt.push({ id, grund: 'Journal der Quelle nicht lesbar' });
            continue;
        }
        const schluessel = klein(roh);
        if ((anzahl.get(schluessel) ?? 0) > 1 || verbleibend.has(schluessel)) {
            // Die lebenden Quellzeilen zu dieser id antworten fuer MEHR als
            // diese eine Zeile; alle stillzulegen naehme womoeglich einer
            // bleibenden oder einer im Ziel nicht angekommenen die Menge.
            ergebnis.nurAngehaengt.push({ id, grund: 'die id steht mehrfach im Protokoll (doppelt_im_projekt)' });
            if (!mehrdeutig.includes(id)) mehrdeutig.push(id);
            continue;
        }
        const journalIds = quellzeilen.get(schluessel) ?? [];
        if (journalIds.length === 0) {
            // Nie gespiegelt, oder schon stillgelegt (Wiederholung): nichts zu tun.
            ergebnis.nurAngehaengt.push({ id, grund: 'in der Quelle lebt keine Zeile zu dieser id' });
            continue;
        }
        kandidaten.push({ id, journalIds });
    }

    // ── 5. Stilllegen in der Quelle — EIN Aufruf, nur Journal-ids ───────────
    // `nurJournalIds`: die Aufloesung ueber `legacy_entry_id` darf nicht
    // greifen, die Werte sind schon die Zeilen selbst. Ein Aufruf und nicht
    // einer je Zeile: `voidJournalEntries` liest dafuer jedes Mal das ganze
    // Journal der Quelle.
    if (kandidaten.length > 0) {
        const ids = [...new Set(kandidaten.flatMap(k => k.journalIds))];
        const stillgelegt = await stilllegenSicher(quelle, ids);
        if (stillgelegt.error || !stillgelegt.journalGelesen) {
            console.warn(
                `[linkTransferJournal] Stilllegen in der Quelle ${quelle} gescheitert — ${kandidaten.length} Zeile(n) `
                + 'leben jetzt in Quelle UND Ziel. Sichtbar im Nachtlauf, keine verlorene Menge.',
                stillgelegt.error,
            );
            for (const k of kandidaten) ergebnis.nurAngehaengt.push({ id: k.id, grund: 'Stilllegen in der Quelle gescheitert' });
        } else {
            if (stillgelegt.voided < ids.length) {
                // Inzwischen von anderer Seite stillgelegt (zaehlt nicht mit),
                // oder nicht mehr auffindbar. Kein Schaden: die Zielzeile lebt.
                console.warn(
                    `[linkTransferJournal] In der Quelle ${quelle} erwartet ${ids.length} stillzulegende Zeile(n), `
                    + `stillgelegt ${stillgelegt.voided}.`,
                    stillgelegt.unbekannt,
                );
            }
            for (const k of kandidaten) ergebnis.verschoben.push(k.id);
        }
    }

    // ── Melden, gesammelt ───────────────────────────────────────────────────
    // Eine Meldung je Klasse und Vorgang, nicht je Zeile: ein ganzes Projekt,
    // das zum zweiten Mal verknuepft wird, haette sonst den Meldekanal (20 je
    // Sitzung) mit einem einzigen Befund gefuellt.
    if (nichtLebend.length > 0) {
        const liste = nichtLebend.map(n => `${n.id}: ${n.grund}`).join('; ');
        console.warn(
            `[linkTransferJournal] ${nichtLebend.length} gewanderte Zeile(n) leben im Ziel ${ziel} nicht — in der Quelle `
            + `${quelle || '(keine)'} NICHT stillgelegt, dort bleiben sie lebend (falscher Ort, keine verlorene Menge; `
            + `der Nachtlauf zeigt sie). ${liste}`,
        );
        void meldeSpiegelFehler(
            ziel,
            nichtLebend.map(n => n.zeile).filter((z): z is ServiceJournalRow => !!z),
            new Error(`Projektwechsel nicht gespiegelt (Quelle ${quelle || 'keine'}): ${liste}`),
            'Anhaengen',
        );
    }
    if (mehrdeutig.length > 0) {
        console.warn(
            `[linkTransferJournal] ${mehrdeutig.length} id(s) stehen mehrfach im Protokoll — in der Quelle ${quelle} `
            + `NICHT stillgelegt (Quarantäneklasse doppelt_im_projekt): ${mehrdeutig.join(', ')}`,
        );
        void meldeSpiegelFehler(
            ziel,
            [],
            new Error(`Projektwechsel: id mehrfach, Quelle ${quelle} nicht stillgelegt: ${mehrdeutig.join(', ')}`),
            'Anhaengen',
        );
    }

    return ergebnis;
};
